import { useQuery } from "@tanstack/react-query"
import { ArrowRight, Briefcase, Building2, MapPin } from "lucide-react"
import { Link, useParams } from "react-router-dom"
import { api } from "@/api/client"
import { Badge } from "@/components/ui/badge"
import { EmptyState } from "@/components/shared/empty-state"
import { CardListSkeleton } from "@/components/shared/loading-skeletons"

type Resp = {
  organization: { id: string; name: string; logo_url: string | null; slug: string }
  jobs: {
    id: string
    title: string
    slug: string
    location: string | null
    employment_type: string
    department: string | null
  }[]
}

const EMPLOYMENT_LABEL: Record<string, string> = {
  full_time: "Tam zamanlı",
  part_time: "Yarı zamanlı",
  contract: "Sözleşmeli",
  internship: "Stajyer",
}

export function CareersOrgPage() {
  const { orgSlug = "" } = useParams()
  const q = useQuery({
    queryKey: ["careers", orgSlug],
    queryFn: () => api<Resp>(`/public/careers/${orgSlug}`),
    enabled: !!orgSlug,
  })

  return (
    <div className="min-h-screen bg-background">
      <section className="brand-gradient border-b">
        <div className="mx-auto max-w-4xl px-6 py-16 sm:py-20">
          {q.data?.organization.logo_url ? (
            <img
              src={q.data.organization.logo_url}
              alt={q.data.organization.name}
              className="mb-5 size-16 rounded-xl border bg-card object-contain p-2"
            />
          ) : (
            <div className="mb-5 grid size-16 place-items-center rounded-xl border bg-card text-muted-foreground">
              <Building2 className="size-7" />
            </div>
          )}
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {q.data?.organization.name ?? "—"}
          </h1>
          <p className="mt-2 text-muted-foreground">
            Açık pozisyonlar — ekibimize katıl, birlikte büyüyelim.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-6 py-10">
        {q.isLoading && <CardListSkeleton count={4} />}
        {q.isError && (
          <EmptyState
            icon={Briefcase}
            title="Şirket bulunamadı"
            description="URL'i kontrol et veya şirketin kariyer sayfasını sor."
          />
        )}
        {q.data && q.data.jobs.length === 0 && (
          <EmptyState
            icon={Briefcase}
            title="Şu an açık ilan yok"
            description="Yeni pozisyonlar açıldığında burada görünecek."
          />
        )}
        <div className="space-y-3">
          {q.data?.jobs.map((job) => (
            <Link
              key={job.id}
              to={`/careers/${q.data!.organization.slug}/${job.slug}`}
              className="group flex items-center gap-4 rounded-xl border bg-card p-5 transition-all hover:border-primary/40 hover:shadow-md"
            >
              <div className="grid size-12 shrink-0 place-items-center rounded-lg bg-primary-soft text-primary">
                <Briefcase className="size-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold tracking-tight">{job.title}</div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  {job.location && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="size-3" /> {job.location}
                    </span>
                  )}
                  <Badge variant="secondary" className="text-[10px]">
                    {EMPLOYMENT_LABEL[job.employment_type] ?? job.employment_type}
                  </Badge>
                  {job.department && (
                    <span className="text-xs">· {job.department}</span>
                  )}
                </div>
              </div>
              <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
            </Link>
          ))}
        </div>
      </div>

      <footer className="border-t py-8 text-center text-xs text-muted-foreground">
        Hirefy ile hızlandırılmış işe alım
      </footer>
    </div>
  )
}
